// InfoHint — ícono ⓘ con explicación al pasar el mouse.
//
// El patrón de autoexplicación del sistema: cada métrica/columna que no se
// entiende con solo el nombre lleva un InfoHint al lado. La meta es que nadie
// necesite capacitación para leer un número — el "¿qué es esto?" se responde
// en el lugar, sin clics ni navegación.
//
// Reusa Popover (hover-based, con delay + auto-flip). Accesible vía teclado:
// el trigger es un <button> enfocable; el contenido aparece también con focus.

import type { ReactNode } from 'react';
import { Icon } from './icon';
import { Popover } from './popover';

export interface InfoHintProps {
  /** Texto o nodo explicativo. Cortito y en castellano llano. */
  children: ReactNode;
  /** Título opcional en negrita arriba de la explicación. */
  label?: string;
  /** Tamaño del ícono en px (default 13). */
  size?: number;
  /** Ancho del tooltip (default 230). */
  width?: number;
  /** Etiqueta accesible del trigger (default "Más información"). */
  ariaLabel?: string;
}

export function InfoHint({
  children,
  label,
  size = 13,
  width = 230,
  ariaLabel = 'Más información',
}: InfoHintProps) {
  return (
    <Popover
      width={width}
      trigger={
        <button
          type="button"
          aria-label={ariaLabel}
          className="inline-flex cursor-help items-center align-middle text-fg-tertiary transition-colors hover:text-fg-secondary focus-visible:text-fg-secondary focus-visible:outline-none"
        >
          <Icon name="info" size={size} stroke={1.75} />
        </button>
      }
      content={
        // Reset tipográfico completo: el hint vive dentro de encabezados con
        // font-mono/uppercase/tracking y esas propiedades SE HEREDAN — sin el
        // reset el tooltip salía gritado en mono y desbordado.
        <div className="space-y-1 text-left font-sans normal-case tracking-normal">
          {label ? (
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-fg-secondary">
              {label}
            </p>
          ) : null}
          <p className="text-[11.5px] font-normal leading-snug text-fg-secondary">{children}</p>
        </div>
      }
    />
  );
}
